import { useEffect, useRef, useState } from "react";
import { useSignUp } from "@clerk/clerk-react";

/**
 * Monitors Clerk signup modal for stuck states (form submission hanging)
 * and provides recovery mechanisms.
 *
 * Returns error state and recovery functions for signup failures in
 * embedded browser contexts.
 */
export const useSignupErrorRecovery = () => {
  const { isLoaded } = useSignUp();
  const [signupError, setSignupError] = useState(null);
  const [isStuck, setIsStuck] = useState(false);
  const submissionTimeoutRef = useRef(null);
  const modalObserverRef = useRef(null);

  useEffect(() => {
    if (!isLoaded) return;

    // Detect modal submission getting stuck (form loading > 10s without response)
    const detectStuckSubmission = () => {
      // Look for Clerk form element that is in loading state
      const clerkModal = document.querySelector(
        '[role="dialog"][data-testid*="signUp"], iframe[src*="accounts.dev"]'
      );

      if (!clerkModal) return;

      // Monitor for long-running form submissions
      const observer = new MutationObserver((mutations) => {
        mutations.forEach((mutation) => {
          if (mutation.type === "attributes") {
            const target = mutation.target;

            // Detect button in loading/disabled state
            if (
              target.tagName === "BUTTON" &&
              (target.disabled || target.getAttribute("data-loading") === "true")
            ) {
              // Set a 10-second timeout; if still loading, mark as stuck
              if (submissionTimeoutRef.current) {
                clearTimeout(submissionTimeoutRef.current);
              }

              submissionTimeoutRef.current = setTimeout(() => {
                console.warn(
                  "[useSignupErrorRecovery] Signup form stuck in loading state for 10+ seconds"
                );
                setIsStuck(true);
                setSignupError(
                  "Signup is taking longer than expected. Try refreshing or using OAuth."
                );
              }, 10000);
            } else if (!target.disabled) {
              // Form recovered, clear timeout
              if (submissionTimeoutRef.current) {
                clearTimeout(submissionTimeoutRef.current);
                submissionTimeoutRef.current = null;
                setIsStuck(false);
              }
            }
          }
        });
      });

      observer.observe(clerkModal, {
        attributes: true,
        subtree: true,
        attributeFilter: ["disabled", "data-loading", "aria-busy"],
      });

      modalObserverRef.current = observer;
    };

    detectStuckSubmission();

    return () => {
      if (modalObserverRef.current) {
        modalObserverRef.current.disconnect();
      }
      if (submissionTimeoutRef.current) {
        clearTimeout(submissionTimeoutRef.current);
      }
    };
  }, [isLoaded]);

  const recoveryOptions = {
    // Reload the entire signup modal
    resetModal: () => {
      setIsStuck(false);
      setSignupError(null);
      window.location.reload();
    },

    // Clear the error state to let user retry
    retry: () => {
      setSignupError(null);
      setIsStuck(false);
    },

    // Suggest using OAuth instead
    suggestOAuth: () => {
      setSignupError(
        "Email signup seems blocked in this browser. Try Google or Facebook OAuth instead."
      );
    },
  };

  return {
    signupError,
    isStuck,
    ...recoveryOptions,
  };
};
